import { DateOnly } from '@if/core/date'
import { PaymentConfig } from '@src/types/loan/loan-calendar'
import { useMemo } from 'react'

/**
 * Hook to find the active payment configuration based on the effective date.
 * Returns the payment config with the most recent effectiveDate that is less than or equal to the given date.
 * 
 * @param paymentConfigs - Array of payment configurations
 * @param referenceDate - Optional date to use as reference. If not provided, uses current date.
 * @returns The active PaymentConfig or null if no config is effective before the reference date
 */
export const useActivePaymentConfig = (
  paymentConfigs: PaymentConfig[],
  referenceDate?: DateOnly,
): PaymentConfig | null => {
  return useMemo(() => {
    if (!paymentConfigs || paymentConfigs.length === 0) {
      return null
    }

    const currentDate = referenceDate ?? new DateOnly()

    let activeConfig: PaymentConfig | null = null

    for (const config of paymentConfigs) {
      if (config.effectiveDate <= currentDate && (activeConfig === null || config.effectiveDate > activeConfig.effectiveDate)) {
        activeConfig = config
      }
    }

    return activeConfig
  }, [paymentConfigs, referenceDate])
}
